<template>
  <div class="comic-translate-container">
    <!-- 背景层 -->
    <div class="bg-layer">
      <div class="bg-image"></div>
      <div class="bg-gradient"></div>
    </div>

    <!-- 导航栏 -->
    <ComicNavbar />

    <!-- 粒子效果 -->
    <Particles />

    <!-- 主内容区域 -->
    <div class="main-content">
      <div class="container">
        <h1 class="page-title"><i class="fas fa-crown crown-icon"></i> {{ $t('manga.vip.pageTitle') }}</h1>
        <p class="page-desc">{{ $t('manga.vip.pageDesc') }}</p>

        <!-- VIP队列状态卡片 -->
        <VipQueueCard :speed-count="speedCount" :vip-count="vipCount" />

        <!-- 上传和预览区域 -->
        <div class="upload-preview-row">
          <!-- 上传区域 -->
          <div class="card upload-card">
            <h3 class="card-title"><i class="fas fa-cloud-upload-alt"></i> {{ $t('manga.vip.uploadTitle') }}</h3>
            <LeftTop class="upload-component" @file-change="handleFileChange" :is-translating="isTranslating"
              :is-vip="true" ref="leftTopRef" />
          </div>

          <!-- 预览区域 -->
          <div class="card preview-card">
            <h3 class="card-title"><i class="fas fa-eye"></i> {{ $t('manga.vip.previewTitle') }}
              <el-tooltip placement="top" :show-after="200" popper-class="upload-limit-tooltip">
                <template #content>
                  <div style="max-width: 240px; line-height: 1.8; font-size: 13px; color: #e2e8f0;">
                    <div style="margin-bottom: 4px;">{{ $t('manga.vip.maxImages') }}</div>
                    <div style="margin-bottom: 4px;">{{ $t('manga.vip.eachLimit') }}</div>
                    <div>{{ $t('manga.vip.totalLimit') }}</div>
                  </div>
                </template>
                <i class="fas fa-question-circle help-icon"></i>
              </el-tooltip>
            </h3>
            <VipRightPanel ref="rightPanelRef" :uploaded-image="uploadedImage" :temp-file="tempFile"
              :queue-count="speedCount" :is-translating="isTranslating" :on-file-added="handleFileAddedFromPanel"
              :on-translation-status-change="handleTranslationStatusChange" :uploaded-images="uploadedImages"
              :all-files="allFiles" :current-index="currentImageIndex" :online-edit="onlineEdit"
              @file-change="handleFileChange" @select-image="handleSelectImage" @clear-all="handleClearAll"
              @open-config="openConfigDialog"
              class="preview-component" />
          </div>
        </div>

        <!-- 翻译历史列表 -->
        <div class="translate-history-section" ref="historySectionRef">
          <div class="translate-list">
            <div class="list-header">
              <h3 class="card-title"><i class="fas fa-list"></i> {{ $t('manga.vip.historyTitle') }}</h3>
              <div class="header-actions">
                <el-button class="clear-btn" type="danger" plain @click="handleClearHistory"
                  :disabled="isClearing">
                  {{ isClearing ? $t('manga.vip.clearHistory') : $t('manga.vip.clear') }}
                </el-button>
                <div class="batch-download-wrap">
                  <el-tooltip placement="top" :show-after="200" popper-class="upload-limit-tooltip">
                    <template #content>
                      <div style="color: #e2e8f0; font-size: 13px;">{{ $t('manga.vip.batchMax') }}</div>
                    </template>
                    <el-button class="batch-download-btn" type="primary" plain @click="handleBatchDownload"
                      :disabled="selectedFileIds.length === 0 || isBatchDownloading">
                      {{ isBatchDownloading ? $t('translate.downloading') : $t('translate.batchDownload') }} ({{ selectedFileIds.length }})
                    </el-button>
                  </el-tooltip>
                  <div v-if="isBatchDownloading" class="download-progress-bar">
                    <div class="download-progress-fill" :style="{ width: downloadProgress + '%' }"></div>
                    <span class="download-progress-text">{{ downloadProgress }}%</span>
                  </div>
                </div>
                <el-button class="refresh-btn" type="info" plain @click="fetchTranslationHisList" :title="$t('manga.vip.refresh')">
                  <i class="fas fa-sync-alt"></i>
                </el-button>
              </div>
            </div>

            <!-- 搜索条件 -->
            <div class="search-form">
              <el-input v-model="searchFileName" :placeholder="$t('manga.vip.searchPlaceholder')" clearable class="search-input"
                @clear="handleSearch" @keyup.enter="handleSearch">
                <template #prefix><i class="fas fa-search"></i></template>
              </el-input>
              <el-select v-model="searchStatus" :placeholder="$t('manga.vip.statusLabel')" clearable class="search-select"
                @change="handleSearch">
                <el-option :label="$t('manga.vip.waiting')" :value="0" />
                <el-option :label="$t('manga.vip.translating')" :value="1" />
                <el-option :label="$t('manga.vip.failed')" :value="2" />
                <el-option :label="$t('manga.vip.completed')" :value="3" />
              </el-select>
              <el-button class="search-btn" type="primary" @click="handleSearch">{{ $t('manga.vip.query') }}</el-button>
              <el-button class="reset-btn" @click="handleResetSearch">{{ $t('manga.vip.reset') }}</el-button>
            </div>

            <!-- 翻译历史表格 -->
            <el-table :data="fileList"
              @selection-change="handleSelectionChange" :empty-text="$t('manga.vip.noRecords')">
              <el-table-column type="selection" width="50" :selectable="(row) => row.status === 3 && row.fileStatus !== 2" />
              <el-table-column prop="fileName" :label="$t('manga.vip.fileName')" min-width="200" show-overflow-tooltip />
              <el-table-column :label="$t('manga.vip.translateStatus')" width="120" align="center">
                <template #default="{ row }">
                  <span :class="getStatusClass(row)">{{ getStatusText(row) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('manga.vip.currentQueue')" width="100" align="center">
                <template #default="{ row }">
                  <span>{{ row.currentQueue ?? '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('manga.vip.creditsCost')" width="100" align="center">
                <template #default="{ row }">
                  <span>{{ row.power ?? '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('manga.vip.targetLanguage')" width="100" align="center">
                <template #default="{ row }">
                  <span>{{ getLanguageText(row.targetLanguage) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('manga.vip.action')" width="280" align="center" >
                <template #default="{ row }">
                  <el-button v-if="row.status === 3 && row.fileStatus !== 2" class="action-btn view-btn" @click="previewFile(row)" size="small"
                    circle>
                    <i class="fas fa-fw fa-search"></i>
                  </el-button>
                  <el-button v-if="canOnlineEdit(row)" class="action-btn edit-btn" @click="openEditDialog(row)" size="small"
                    circle :title="$t('manga.vip.onlineEdit')" :loading="editLoadingId === row.id">
                    <i v-if="editLoadingId !== row.id" class="fas fa-fw fa-edit"></i>
                  </el-button>
                  <el-button v-if="row.status === 3 && row.fileStatus !== 2" class="action-btn download-btn" @click="downloadFile(row)"
                    size="small" circle>
                    <i class="fas fa-fw fa-download"></i>
                  </el-button>
                  <el-button v-if="row.status !== 0 && row.status !== 1" class="action-btn delete-btn" @click="handleDeleteRow(row)"
                    size="small" circle>
                    <i class="fas fa-fw fa-trash-alt"></i>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-wrapper">
              <Pagination :total="pagination.total" :page="pagination.pageNo" :size="pagination.pageSize"
                @pagination="handlePagination" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 清除浮动 -->
    <div style="clear: both;"></div>

    <!-- 底部 -->
    <ComicFooter @show-modal="showModal" />

    <!-- 蒙版预览 -->
    <Teleport to="body">
      <MangaEditDialog
        :visible="editDialogVisible"
        :row="editDialogRow"
        :preview-id="previewBase"
        @close="closeEditDialog"
      />

      <div v-if="previewMaskVisible" class="mask-preview-container" @click="closePreviewMask">
        <img :src="previewImageUrl" class="mask-preview-image" @click.stop />
      </div>
    </Teleport>

    <!-- 弹窗 -->
    <ComicModal :is-visible="modalVisible" :title="modalTitle" :modal-type="modalType" @close="closeModal" />

    <!-- 翻译配置弹窗 -->
    <ComicModal :is-visible="configDialogVisible" :title="$t('manga.vip.configTitle')" modal-type="config" @close="configDialogVisible = false">
        <div class="config-form">
          <label class="config-label">{{ $t('manga.vip.configLangLabel') }}</label>
          <el-select v-model="targetLanguage" :placeholder="$t('manga.vip.configLangPlaceholder')" style="width: 100%;" popper-class="config-select-popper">
            <el-option v-for="item in languageOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <label class="config-label">{{ $t('manga.vip.configLayoutLabel') }}</label>
          <el-select v-model="layoutMode" :placeholder="$t('manga.vip.configLayoutPlaceholder')" style="width: 100%;" clearable popper-class="config-select-popper">
            <el-option v-for="item in layoutModeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <label class="config-label">{{ $t('manga.vip.configOnlineEditLabel') }}</label>
          <el-select v-model="onlineEdit" style="width: 100%;" popper-class="config-select-popper">
            <el-option :label="$t('manga.vip.configOnlineEditOn')" :value="true" />
            <el-option :label="$t('manga.vip.configOnlineEditOff')" :value="false" />
          </el-select>
        </div>
      <template #footer>
        <div class="config-footer">
          <el-button type="primary" @click="handleConfigConfirm">{{ $t('manga.vip.configConfirm') }}</el-button>
        </div>
      </template>
    </ComicModal>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, provide, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import LeftTop from '@/components/manga/LeftTop.vue'
import VipRightPanel from '@/components/manga/VipRightPanel.vue'
import ComicNavbar from '@/components/manga/ComicNavbar.vue'
import Particles from '@/components/Particles.vue'
import ComicFooter from '@/components/manga/ComicFooter.vue'
import ComicModal from '@/components/manga/ComicModal.vue'
import MangaEditDialog from '@/components/manga/MangaEditDialog.vue'
import VipQueueCard from '@/components/manga/VipQueueCard.vue'

// 导入API接口
import { downloadManga, batchDownloadManga } from '@/api/upload.js'
import { getUserTranslationHisList, getUserTranslationHisById, deleteUserTranslationHis, clearUserTranslationHis, getVipTeam } from '@/api/manga/manga.js'
import { getDictItemsByDictType } from '@/api/admin/dictItem.js'

// 导入上传接口（需要用于provide）
import { uploadMultipleManga } from '@/api/upload.js'

// 导入SEO工具函数

const route = useRoute()
const { t } = useI18n()

// ====================== 核心数据定义 ======================
const tempFile = ref(null)
const uploadedImage = ref('')
const uploadedImages = ref([])
const allFiles = ref([])
const currentImageIndex = ref(0)

const fileList = ref([])
const isTranslating = ref(false)
const speedCount = ref(0)
const vipCount = ref(0)
const queueCheckTimer = ref(null)
const rightPanelRef = ref(null)
const leftTopRef = ref(null)
const historySectionRef = ref(null)
const selectedFileIds = ref([]) // 存储选中的文件ID
const isBatchDownloading = ref(false) // 批量下载状态控制
const downloadProgress = ref(0) // 批量下载进度 0-100
const isClearing = ref(false) // 清空状态控制

// 翻译配置相关
const configDialogVisible = ref(false)
const targetLanguage = ref(localStorage.getItem('vipTargetLanguage') || 'CHS')
const languageOptions = ref([])
const layoutMode = ref(localStorage.getItem('vipLayoutMode') || 'balloon_fill')
const layoutModeOptions = ref([])
const onlineEdit = ref(localStorage.getItem('vipOnlineEdit') === 'true')

// 蒙版预览相关变量
const previewBase = (window.global_config && window.global_config.previewId) || ''
const editDialogVisible = ref(false)
const editDialogRow = ref(null)
const editLoadingId = ref(null)
const previewMaskVisible = ref(false) // 蒙版显隐控制
const previewImageUrl = ref('') // 预览图片地址

// 分页和搜索相关
const pagination = ref({
  pageNo: 1,
  pageSize: 10,
  total: 0
})
const searchFileName = ref('')
const searchStatus = ref(null)


// ====================== 状态映射 ======================
const getStatusText = (row) => {
  if (row.status === 3 && row.fileStatus === 2) {
    return t('manga.vip.statusExpired')
  }
  const map = { 0: t('manga.vip.statusWaiting'), 1: t('manga.vip.statusTranslating'), 2: t('manga.vip.statusFailed'), 3: t('manga.vip.statusCompleted') }
  return map[row.status] || t('manga.vip.statusUnknown')
}

const getLanguageText = (lang) => {
  const item = languageOptions.value.find(opt => opt.value === lang)
  return item ? item.label : t('manga.vip.defaultLang')
}

const getStatusClass = (row) => {
  // status=3 且 fileStatus=2 时显示过期样式
  if (row.status === 3 && row.fileStatus === 2) {
    return 'status-expired'
  }
  switch (row.status) {
    case 1: return 'status-translating'
    case 3: return 'status-completed'
    case 2: return 'status-failed'
    default: return 'status-waiting'
  }
}

// ====================== 获取翻译历史列表 ======================
const fetchTranslationHisList = async () => {
  try {
    const params = {
      pageNo: pagination.value.pageNo,
      pageSize: pagination.value.pageSize,
      channelType: 1
    }
    if (searchFileName.value) {
      params.fileName = searchFileName.value
    }
    if (searchStatus.value !== null && searchStatus.value !== '') {
      params.status = searchStatus.value
    }
    const res = await getUserTranslationHisList(params)
    if (res.code === 200 && res.data) {
      fileList.value = res.data.records || []
      pagination.value.total = Number(res.data.total) || 0
    }
  } catch (error) {
    console.error('获取翻译历史列表失败：', error)
  }
}

// ====================== 搜索和分页 ======================
const handleSearch = () => {
  pagination.value.pageNo = 1
  fetchTranslationHisList()
}

const handleResetSearch = () => {
  searchFileName.value = ''
  searchStatus.value = null
  pagination.value.pageNo = 1
  fetchTranslationHisList()
}

const handlePagination = ({ page, size }) => {
  pagination.value.pageNo = page
  pagination.value.pageSize = size
  fetchTranslationHisList()
}

// ====================== 获取目标语言字典 ======================
const fetchLanguageOptions = async () => {
  try {
    const res = await getDictItemsByDictType('target_language')
    if (res.code === 200 && res.data) {
      languageOptions.value = res.data.map(item => ({
        label: item.dictLabel,
        value: item.dictValue
      }))
      // 默认选中简体中文
      if (!targetLanguage.value || !languageOptions.value.some(item => item.value === targetLanguage.value)) {
        targetLanguage.value = 'CHS'
      }
    }
  } catch (error) {
    console.error('获取目标语言字典失败：', error)
  }
}

// ====================== 获取排版模式字典 ======================
const fetchLayoutModeOptions = async () => {
  try {
    const res = await getDictItemsByDictType('layout_mode')
    if (res.code === 200 && res.data) {
      layoutModeOptions.value = res.data.map(item => ({
        label: item.dictLabel,
        value: item.dictValue
      }))
    }
  } catch (error) {
    console.error('获取排版模式字典失败：', error)
  }
}

onMounted(() => {
  fetchTranslationHisList()
  fetchQueueCount()
  fetchLanguageOptions()
  fetchLayoutModeOptions()
  queueCheckTimer.value = setInterval(fetchQueueCount, 30000)
  // 添加ESC按键监听
  document.addEventListener('keydown', handleEscKey)

  // 如果是从"我的翻译"跳转过来，滚动到翻译历史列表
  if (route.query.scrollTo === 'history') {
    nextTick(() => {
      if (historySectionRef.value) {
        historySectionRef.value.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
})

// 监听路由参数变化，已在VIP页面时点击"我的翻译"也能滚动到历史列表
watch(() => route.query.scrollTo, (val) => {
  if (val === 'history') {
    nextTick(() => {
      if (historySectionRef.value) {
        historySectionRef.value.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
})

// ====================== 轮询获取排队人数 ======================
const fetchQueueCount = async () => {
  try {
    const res = await getVipTeam()
    if (res.code === 200) {
      speedCount.value = Number(res.data?.speed) || 0
      vipCount.value = Number(res.data?.vip) || 0
    }
  } catch (error) {
    console.error('获取VIP排队人数失败：', error)
  }
}

// ====================== 预览文件方法 ======================
const previewFile = (file) => {
  if (!file.id) {
    ElMessage.error(t('manga.vip.fileIdMissing'))
    return
  }
  // 拼接预览地址
  const ext = file.fileName.substring(file.fileName.lastIndexOf('.'))
  previewImageUrl.value = `${window.global_config.previewId}/${file.id}${ext}`
  // 显示蒙版预览
  previewMaskVisible.value = true
}

// ====================== 关闭蒙版预览方法 ======================
// 列表接口不再返回 translationJson，用 config.onlineEdit 判断是否可在线编辑
const canOnlineEdit = (row) => {
  if (row.status !== 3) return false
  try {
    return JSON.parse(row.config || '{}').onlineEdit === true
  } catch (e) {
    return false
  }
}

const openEditDialog = async (row) => {
  if (editLoadingId.value) return
  editLoadingId.value = row.id
  try {
    const res = await getUserTranslationHisById(row.id)
    const detail = res.code === 200 ? res.data : null
    if (!detail || !detail.translationJson) {
      ElMessage.warning(t('manga.vip.editDataMissing'))
      return
    }
    editDialogRow.value = { ...row, translationJson: detail.translationJson }
    editDialogVisible.value = true
  } catch (error) {
    console.error('获取编辑数据失败：', error)
    ElMessage.warning(t('manga.vip.editDataMissing'))
  } finally {
    editLoadingId.value = null
  }
}

const closeEditDialog = () => {
  editDialogVisible.value = false
  editDialogRow.value = null
}

const closePreviewMask = () => {
  previewMaskVisible.value = false
  previewImageUrl.value = ''
}

// ====================== ESC按键监听方法 ======================
const handleEscKey = (e) => {
  if (e.key === 'Escape' && previewMaskVisible.value) {
    closePreviewMask()
  }
}

// ====================== 文件变更处理 ======================
const handleFileChange = ({ file, imageUrl, allFiles: files, allImages, clearAll }) => {
  tempFile.value = file
  uploadedImage.value = imageUrl

  // 如果是清空操作，重置所有状态
  if (clearAll) {
    uploadedImages.value = []
    allFiles.value = []
    currentImageIndex.value = 0
    return
  }

  uploadedImages.value = allImages || []
  allFiles.value = files || []
  if (allImages && allImages.length > 0) {
    currentImageIndex.value = allImages.length - 1
  } else {
    currentImageIndex.value = 0
  }
}

// ====================== 选择图片处理 ======================
const handleSelectImage = ({ index, imageUrl }) => {
  currentImageIndex.value = index
  uploadedImage.value = imageUrl
}


// ====================== 翻译状态变更处理 ======================
const handleTranslationStatusChange = (translating) => {
  isTranslating.value = translating
}

// ====================== 翻译完成回调（从RightPanel） ======================
const handleFileAddedFromPanel = () => {
  // 翻译完成后刷新列表
  fetchTranslationHisList()
  // 刷新用户信息（算力等）
  window.dispatchEvent(new Event('refresh-user-info'))
}

// ====================== 清空图片预览 ======================
const clearImagePreview = () => {
  uploadedImage.value = ''
  tempFile.value = null

  // 清空预览蒙版状态
  previewMaskVisible.value = false
  previewImageUrl.value = ''

  // 同步清空右侧面板的预览
  if (rightPanelRef.value && typeof rightPanelRef.value.clearAllImages === 'function') {
    rightPanelRef.value.clearAllImages()
  }
}


// ====================== 清除所有图片 ======================
const handleClearAll = () => {
  // 清空所有相关状态，确保第二次上传时状态正确
  uploadedImages.value = []
  allFiles.value = []
  currentImageIndex.value = 0
  clearImagePreview()

  // 同步清空左侧上传组件的状态
  if (leftTopRef.value && typeof leftTopRef.value.clearAllFiles === 'function') {
    leftTopRef.value.clearAllFiles()
  }
}

// ====================== 下载文件功能 ======================
const downloadFile = async (file) => {
  if (file.status !== 3) {
    ElMessage.error(t('manga.vip.fileNotComplete'))
    return
  }
  if (!file.id) {
    ElMessage.error(t('manga.vip.fileIdMissing'))
    return
  }

  try {
    const response = await downloadManga(file.id, 'manga_vip')
    const blob = new Blob([response.data])
    let fileName = file.fileName
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const matches = /filename="?([^";]+)"?/.exec(contentDisposition)
      if (matches && matches[1]) {
        fileName = decodeURIComponent(matches[1])
      }
    }

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    ElMessage.success(`VIP ${t('translate.downloadSuccess')}: ${fileName}`)
  } catch (error) {
    console.error('VIP download failed:', error)
    if (error.response) {
      if (error.response.status === 404) {
        ElMessage.error(t('manga.vip.fileNotFound'))
      } else if (error.response.status === 500) {
        ElMessage.error(t('manga.vip.serverError'))
      } else {
        ElMessage.error(`${t('translate.downloadFailed')}: HTTP ${error.response.status}`)
      }
    } else if (error.request) {
      ElMessage.error(t('manga.vip.networkError'))
    } else {
      ElMessage.warning(t('manga.vip.noFileFound'))
    }
  }
}

// ====================== 处理选择状态变化 ======================
const handleSelectionChange = (selection) => {
  selectedFileIds.value = selection.map(row => row.id)
}

// ====================== 批量下载功能 ======================
const handleBatchDownload = async () => {
  if (selectedFileIds.value.length === 0) {
    ElMessage.warning(t('manga.vip.selectFilesFirst'))
    return
  }

  // 检查选中的文件是否都是已完成的
  const selectedFiles = fileList.value.filter(file => selectedFileIds.value.includes(file.id))
  const incompleteFiles = selectedFiles.filter(file => file.status !== 3)

  if (incompleteFiles.length > 0) {
    ElMessage.warning(t('manga.vip.incompleteFiles', { count: incompleteFiles.length }))
    return
  }

  // 如果正在下载中，直接返回，防止重复操作
  if (isBatchDownloading.value) {
    ElMessage.warning(t('manga.vip.downloadingWait'))
    return
  }

  try {
    // 设置下载状态为true，禁用按钮
    isBatchDownloading.value = true
    downloadProgress.value = 0
    ElMessage.success(t('manga.vip.batchDownloadStart', { count: selectedFileIds.value.length }))
    const response = await batchDownloadManga(selectedFileIds.value, 'manga_vip', (progress) => {
      downloadProgress.value = progress
    })

    const blob = new Blob([response.data])

    // 生成下载文件名
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    const fileName = `VIP漫画翻译批量下载_${timestamp}.zip`

    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    // 清理
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    ElMessage.success(`VIP批量下载成功: ${fileName}`)
  } catch (error) {
    console.error('VIP批量下载失败详情：', {
      error: error,
      message: error.message,
      response: error.response,
      request: error.request,
      selectedIds: selectedFileIds.value
    })

    if (error.response) {
      console.error('server response error:', error.response)
      if (error.response.status === 400) {
        ElMessage.error(t('manga.vip.paramError'))
      } else if (error.response.status === 500) {
        const errorMessage = error.response.data ?
          (typeof error.response.data === 'string' ? error.response.data : t('translate.serverError')) :
          t('translate.serverError')
        ElMessage.error(errorMessage)
      } else if (error.response.data) {
        try {
          const errorText = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)
          ElMessage.error(`${t('translate.batchDownloadFailed')}: ${errorText}`)
        } catch (e) {
          ElMessage.error(`${t('translate.batchDownloadFailed')}: HTTP ${error.response.status}`)
        }
      } else {
        ElMessage.error(`${t('translate.batchDownloadFailed')}: HTTP ${error.response.status}`)
      }
    } else if (error.request) {
      ElMessage.error(t('manga.vip.networkError'))
    } else {
      ElMessage.error(`${t('translate.batchDownloadFailed')}: ${error.message || t('manga.vip.statusUnknown')}`)
    }
  } finally {
    // 无论成功还是失败，都要重置下载状态，启用按钮
    isBatchDownloading.value = false
    downloadProgress.value = 0
  }
}


// ====================== 删除单条记录 ======================
const handleDeleteRow = async (row) => {
  try {
    await ElMessageBox.confirm(t('manga.vip.deleteConfirm'), t('manga.vip.deleteTitle'), {
      confirmButtonText: t('translate.confirm'),
      cancelButtonText: t('translate.cancel'),
      type: 'warning'
    })
    const res = await deleteUserTranslationHis(row.id)
    if (res.code === 200) {
      ElMessage.success(t('manga.vip.deleteSuccess'))
      fetchTranslationHisList()
    } else {
      ElMessage.error(res.message || t('manga.vip.deleteFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('delete failed:', error)
      ElMessage.error(t('manga.vip.deleteFailed'))
    }
  }
}

// ====================== 清空已完成/失败的记录 ======================
const handleClearHistory = async () => {
  try {
    await ElMessageBox.confirm(t('manga.vip.clearConfirm'), t('manga.vip.deleteTitle'), {
      confirmButtonText: t('translate.confirm'),
      cancelButtonText: t('translate.cancel'),
      type: 'warning'
    })
    isClearing.value = true
    const res = await clearUserTranslationHis()
    if (res.code === 200) {
      ElMessage.success(t('manga.vip.clearSuccess'))
      fetchTranslationHisList()
    } else {
      ElMessage.error(res.message || t('manga.vip.clearFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('clear failed:', error)
      ElMessage.error(t('manga.vip.clearFailed'))
    }
  } finally {
    isClearing.value = false
  }
}

// 弹窗状态管理
const modalVisible = ref(false)
const modalType = ref('')
const modalTitle = ref('')

const showModal = (type) => {
  modalType.value = type
  switch (type) {
    case 'privacy':
      modalTitle.value = t('manga.footer.privacy')
      break
    case 'agreement':
      modalTitle.value = t('manga.footer.agreement')
      break
    case 'about':
      modalTitle.value = t('manga.footer.about')
      break
  }
  modalVisible.value = true
}

const closeModal = () => {
  modalVisible.value = false
  modalType.value = ''
  modalTitle.value = ''
}

const openConfigDialog = () => {
  targetLanguage.value = localStorage.getItem('vipTargetLanguage') || 'CHS'
  layoutMode.value = localStorage.getItem('vipLayoutMode') || 'balloon_fill'
  onlineEdit.value = localStorage.getItem('vipOnlineEdit') === 'true'
  configDialogVisible.value = true
}

const handleConfigConfirm = () => {
  localStorage.setItem('vipTargetLanguage', targetLanguage.value)
  localStorage.setItem('vipLayoutMode', layoutMode.value)
  localStorage.setItem('vipOnlineEdit', String(onlineEdit.value))
  configDialogVisible.value = false
  ElMessage.success(`已保存翻译配置`)
}

// 提供API方法给子组件
provide('uploadMultipleManga', uploadMultipleManga)




onUnmounted(() => {
  if (queueCheckTimer.value) {
    clearInterval(queueCheckTimer.value)
  }
  // 移除ESC按键监听
  document.removeEventListener('keydown', handleEscKey)
})</script>

<style scoped>
.comic-translate-container {
  min-height: 100vh;
  position: relative;
  color: #f1f5f9;
  overflow-x: hidden;
  background: #0a0f1a;
}

.bg-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.bg-layer .bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('~@/assets/images/login.jpg') no-repeat center center;
  background-size: cover;
}

.bg-layer .bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(10, 15, 26, 0.92) 0%, rgba(15, 23, 42, 0.88) 50%, rgba(30, 41, 59, 0.85) 100%);
}

.comic-translate-container > *:not(.bg-layer):not(.bg-particles):not(.navbar) {
  position: relative;
  z-index: 1;
}

.bg-particles {
  z-index: 1;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem 0;
  width: 100%;
  box-sizing: border-box;
}

.page-title {
  text-align: center;
  margin-bottom: 1rem;
  font-size: 2.2rem;
  font-weight: 800;
  background: linear-gradient(90deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 40px rgba(167, 139, 250, 0.3);
  letter-spacing: 2px;
}

.crown-icon {
  color: #fbbf24;
  font-size: 1.8rem;
  margin-right: 8px;
  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.5));
  animation: crownFloat 2s ease-in-out infinite;
}

@keyframes crownFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.page-desc {
  text-align: center;
  color: #94a3b8;
  margin-bottom: 2rem;
  font-size: 1rem;
}

/* 功能布局 */
.upload-preview-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 通用卡片样式 */
.card {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.1);
  border-radius: 16px;
  padding: 1.8rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 50%;
  box-sizing: border-box;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(94, 234, 212, 0.1);
  border-color: rgba(94, 234, 212, 0.3);
}

.card-title {
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0px;
}

.card-title i {
  color: #fbbf24;
}

.help-icon {
  color: #fbbf24;
  font-size: 0.8rem;
  cursor: help;
  margin-left: 4px;
  margin-top: 2px;
  transition: color 0.2s;
  vertical-align: middle;
  line-height: 1;
}

.help-icon:hover {
  color: #fcd34d;
}

:global(.upload-limit-tooltip) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(94, 234, 212, 0.3) !important;
  border-radius: 8px !important;
  padding: 10px 14px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4) !important;
}

:global(.upload-limit-tooltip .el-popper__arrow::before) {
  background: rgba(15, 23, 42, 0.95) !important;
  border-color: rgba(94, 234, 212, 0.3) !important;
}

/* 上传和预览组件样式 */
.upload-component {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.preview-component {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* 翻译历史区域 */
.translate-history-section {
  margin-bottom: 5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 翻译列表样式 */
.translate-list {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.1);
  border-radius: 16px;
  padding: 1.8rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 10px;
}

:deep(.batch-download-btn.el-button--primary.is-plain) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;
}

:deep(.batch-download-btn.el-button--primary.is-plain:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
}

.batch-download-wrap {
  position: relative;
}

.download-progress-bar {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 160px;
  height: 24px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 6px;
  pointer-events: none;
}

.download-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, rgba(94, 234, 212, 0.4), rgba(94, 234, 212, 0.7));
  border-radius: 12px;
  transition: width 0.3s ease;
}

.download-progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  color: #fff;
  font-weight: 500;
}

:deep(.refresh-btn.el-button--info.is-plain) {
  background: transparent !important;
  color: #5eead4 !important;
  border: none !important;
  padding: 0 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

:deep(.refresh-btn.el-button--info.is-plain:hover) {
  color: #a78bfa !important;
  background: transparent !important;
}

:deep(.clear-btn.el-button--danger.is-plain) {
  background: rgba(248, 113, 113, 0.1) !important;
  color: #f87171 !important;
  border: 1px solid rgba(248, 113, 113, 0.2) !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;
}

:deep(.clear-btn.el-button--danger.is-plain:hover) {
  background: rgba(248, 113, 113, 0.2) !important;
}

/* 搜索表单样式 */
.search-form {
  display: flex;
  gap: 12px;
  margin-bottom: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  width: 240px;
}

.search-select {
  width: 150px;
}

:deep(.search-input .el-input__wrapper),
:deep(.search-select .el-input__wrapper) {
  background: rgba(51, 65, 85, 0.6) !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  box-shadow: none !important;
  border-radius: 8px !important;
}

:deep(.search-input .el-input__inner),
:deep(.search-select .el-input__inner) {
  color: #5eead4 !important;
  border-color: #5eead4;
}

:deep(.search-input .el-input__inner::placeholder) {
  color: #64748b !important;
}



:deep(.search-btn.el-button--primary) {
  background: rgba(94, 234, 212, 0.15) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.3) !important;
  border-radius: 8px !important;
}

:deep(.search-btn.el-button--primary:hover) {
  background: rgba(94, 234, 212, 0.25) !important;
}

:deep(.reset-btn) {
  background: rgba(51, 65, 85, 0.6) !important;
  /* color: #94a3b8 !important; */
  border: 1px solid rgba(94, 234, 212, 0.15) !important;
  border-radius: 8px !important;
  margin-left: 0px;
}

:deep(.reset-btn:hover) {
  color: #e2e8f0 !important;
  border-color: rgba(94, 234, 212, 0.3) !important;
}

/* 表格样式（scoped变量 + 非scoped覆盖配合使用） */
:deep(.el-table) {
  --el-color-white: #e2e8f000;
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: rgba(51, 65, 85, 0.5);
  --el-table-row-hover-bg-color: rgba(51, 65, 85, 0.4);
  --el-table-border-color: rgba(94, 234, 212, 0.1);
  --el-table-text-color: #e2e8f0;
  --el-table-header-text-color: #94a3b8;
  --el-table-expanded-cell-bg-color: transparent;
  border-radius: 10px !important;
  overflow: hidden !important;
}

:deep(.el-table__inner-wrapper::before) {
  display: none !important;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #5eead4 !important;
  border-color: #5eead4 !important;
}

:deep(.el-checkbox__inner:hover) {
  border-color: #5eead4 !important;
}

/* 状态标签 */
.status-translating,
.status-completed,
.status-failed,
.status-waiting,
.status-expired {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.6;
}

.status-waiting {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.15);
}

.status-translating {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.15);
}

.status-failed {
  color: #f87171;
  background: rgba(248, 113, 113, 0.15);
}

.status-completed {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.15);
}

.status-expired {
  color: #fb923c;
  background: rgba(251, 146, 60, 0.15);
}

/* 分页样式 */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding: 0.5rem 0;
  overflow-x: auto;
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent !important;
  /* --el-pagination-text-color: #94a3b8 !important; */
  --el-pagination-button-bg-color: rgba(51, 65, 85, 0.6) !important;
  /* --el-pagination-button-color: #94a3b8 !important; */
  --el-pagination-hover-color: #5eead4 !important;
  flex-wrap: wrap;
  justify-content: center;
}

:deep(.el-pagination .el-pager li) {
  background: rgba(51, 65, 85, 0.6) !important;
  border-radius: 6px !important;
  margin: 0 2px;
}

:deep(.el-pagination .el-pager li.is-active) {
  background: rgba(8, 239, 204, 0.2) !important;
  color: #5eead4 !important;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  background: rgba(51, 65, 85, 0.6) !important;
  /* color: #94a3b8 !important; */
  border-radius: 6px !important;
}

:deep(.el-pagination .el-pagination__total),
:deep(.el-pagination .el-pagination__jump) {
  color: #64748b !important;
}

:deep(.el-pagination .el-input__wrapper) {
  background: rgba(51, 65, 85, 0.6) !important;
  box-shadow: none !important;
  border: 1px solid rgba(94, 234, 212, 0.15) !important;
}

:deep(.el-pagination .el-select .el-input .el-input__inner) {
  color: #5eead4 !important;
  border-color: #5eead4 !important;
}

/* 蒙版预览样式优化 */
.mask-preview-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 20px;
  box-sizing: border-box;
}

.mask-preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: default;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* 响应式 */
@media (max-width: 900px) {
  .upload-preview-row {
    flex-direction: column;
    gap: 1.5rem;
  }

  .card {
    max-width: 100%;
  }

  .main-content {
    padding: 5rem 1rem 6rem;
  }

  .container {
    gap: 1.5rem;
  }
}

@media (max-width: 500px) {
  .page-title {
    font-size: 1.5rem;
  }

  .card {
    padding: 1.2rem;
  }

  .list-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}

/* 确保容器正确布局 */
.container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 上传结果弹窗 */
:global(.batch-upload-result-dialog) {
  border-radius: 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__header) {
  padding: 18px 20px 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__title) {
  font-size: 16px !important;
  font-weight: 600 !important;
}

:global(.batch-upload-result-dialog .el-message-box__status) {
  display: none !important;
}

:global(.batch-upload-result-dialog .el-message-box__content) {
  max-height: 400px !important;
  overflow-y: auto !important;
  padding: 0 20px 12px !important;
}

:global(.batch-upload-result-dialog .el-message-box__message) {
  padding: 0 !important;
}

:global(.batch-upload-result-dialog .el-message-box__btns) {
  padding: 8px 20px 18px !important;
}

:global(.upload-error-list) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:global(.upload-error-item) {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #94a3b8;
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.12);
  transition: all 0.15s ease;
  word-break: break-all;
}

:global(.upload-error-item:hover) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}
</style>

<style>
/* 非scoped表格深色覆盖（Element Plus默认背景不透明，需全局覆盖） */
.translate-list .el-table,
.translate-list .el-table__inner-wrapper,
.translate-list .el-table__header-wrapper,
.translate-list .el-table__body-wrapper,
.translate-list .el-table__footer-wrapper,
.translate-list .el-table__empty-block,
.translate-list .el-table-fixed-column--left,
.translate-list .el-table-fixed-column--right {
  background: transparent !important;
}

.translate-list .el-table th.el-table__cell {
  background: rgba(51, 65, 85, 0.5) !important;
  /* color: #94a3b8 !important; */
}

.translate-list .el-table td.el-table__cell {
  background: transparent !important;
  color: #e2e8f0 !important;
}

.translate-list .el-table tr:hover>td.el-table__cell {
  background: rgba(51, 65, 85, 0.4) !important;
}

/* 状态标签颜色：选择器必须比 td.el-table__cell 更具体才能覆盖 */
.translate-list .el-table td.el-table__cell .status-waiting {
  /* color: #94a3b8 !important; */
  background: rgba(148, 163, 184, 0.15);
}

.translate-list .el-table td.el-table__cell .status-translating {
  color: #fbbf24 !important;
  background: rgba(251, 191, 36, 0.15);
}

.translate-list .el-table td.el-table__cell .status-failed {
  color: #f87171 !important;
  background: rgba(248, 113, 113, 0.15);
}

.translate-list .el-table td.el-table__cell .status-completed {
  color: #4ade80 !important;
  background: rgba(74, 222, 128, 0.15);
}

.translate-list .el-table td.el-table__cell .status-expired {
  color: #fb923c !important;
  background: rgba(251, 146, 60, 0.15);
}

/* 操作按钮样式（与免费队列一致） */
/* 操作列按钮统一外观（查看/在线编辑/下载同款，删除单独红色） */
.translate-list .el-table td.el-table__cell .action-btn {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  box-shadow: none !important;
  font-weight: 400 !important;
}

.translate-list .el-table td.el-table__cell .action-btn:hover {
  background: rgba(94, 234, 212, 0.2) !important;
  border-color: rgba(94, 234, 212, 0.35) !important;
  transform: translateY(-2px);
}

.translate-list .el-table td.el-table__cell .action-btn.delete-btn {
  background: rgba(248, 113, 113, 0.1) !important;
  color: #f87171 !important;
  border-color: rgba(248, 113, 113, 0.2) !important;
}

.translate-list .el-table td.el-table__cell .action-btn.delete-btn:hover {
  background: rgba(248, 113, 113, 0.2) !important;
  border-color: rgba(248, 113, 113, 0.35) !important;
}

.config-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-label {
  color: #e2e8f0;
  font-weight: 500;
  font-size: 14px;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.config-footer .el-button--primary {
  background: linear-gradient(90deg, #5eead4, #3b82f6) !important;
  color: #0f172a !important;
  border: none !important;
  font-weight: 600 !important;
}

</style>

<style>
.config-select-popper {
  z-index: 10001 !important;
}
</style>